import React, {useMemo, useState} from 'react';
import {
    MaterialReactTable,
    MRT_ColumnDef,
    MRT_ColumnFiltersState,
    MRT_EditActionButtons,
    MRT_Row,
    useMaterialReactTable,
} from 'material-react-table';
import {Box, Button, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Tooltip,} from '@mui/material';
import {QueryClient} from '@tanstack/react-query';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {VersOreDTO2} from './types';
import {useDeleteVersOre2, useGetVersOreDTO2} from './Services';
import {useTabSettings} from '../../common/Services';
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddVersOre2 from "./AddVersOre2";
import EditVersOre2 from "./EditVersOre2";
import ExpVersOre2 from "./ExpVersOre2";
import AddVersOre2Voice from "./AddVersOre2Voice";


interface ListaVersOre2Props {}

const ListaVersOre2: React.FC<ListaVersOre2Props> = () => {
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [isDialogEditOpen, setIsDialogEditOpen] = useState(false);
  const [currentRowId, setCurrentRowId] = useState(0);
  const [isDialogAddOpen, setIsDialogAddOpen] = useState(false);
  const [isDialogAddVoiceOpen, setIsDialogAddVoiceOpen] = useState(false);
  const [hideVarianteNotNullRows, setHideVarianteNotNullRows] = useState(true);
  const [isDialogExporterOpen, setIsDialogExporterOpen] = useState(false);

  const [duplicati, setDuplicati] = useState(0);

  const keyusername = localStorage.getItem('keyusername') || 'NN';
  const {columnVisibility,  setColumnVisibility,  columnFilters, setColumnFilters,  sorting,  setSorting, pagination,  setPagination,  saveTabSettings,isTabSetReady} = useTabSettings('ListaVersOre2', keyusername);
  const [draftColumnFilters, setDraftColumnFilters] = useState<MRT_ColumnFiltersState>(columnFilters);
  const { mutateAsync: deleteVersore2, isPending: isDeletingVersore2 } = useDeleteVersOre2();


  const {    data: { results = [], count } = {},    isError: isLoadingVersOreDTO2Error,    isFetching: isFetchingVersOreDTO2,    isLoading: isLoadingVersOreDTO2,error  } = useGetVersOreDTO2(pagination, columnFilters, globalFilter, sorting, duplicati, isTabSetReady);

  const coddip = localStorage.getItem('coddip') || '';


const normalizedResults = useMemo(() => {
  return results.map((r) => ({
    ...r,
    variante: r.variante?.trim() || '',
    coddip: r.coddip?.trim() || '',
    codprog: r.codprog?.trim() || '',
    luogo: r.luogo?.trim() || '',
  }));
}, [results]);


const hhmmToMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

const minutesToHHMM = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
};

const orePerDipGiorno = useMemo(() => {
  const map = new Map<string, number>();

  normalizedResults.forEach((row) => {
    if (!row.coddip || !row.datareg) return;

    const date = new Date(row.datareg).toISOString().split('T')[0];
    const key = `${row.coddip}_${date}`;

    const minuti = hhmmToMinutes(row.oremineff ?? '00:00');

    map.set(key, (map.get(key) || 0) + minuti);
  });

  return map;
}, [normalizedResults]);

const effectiveColumnVisibility = useMemo(() => {
  return {
    ...columnVisibility,
    variante: hideVarianteNotNullRows ? false : true,
  };
}, [columnVisibility, hideVarianteNotNullRows]);



  const columns = useMemo<MRT_ColumnDef<VersOreDTO2>[]>(() => [
  { accessorKey: 'id', header: 'NrRel', size: 2, enableEditing: false, },
  { accessorKey: 'variante', header: 'NrRelOrig', size: 2, enableEditing: false, },
  { accessorKey: 'desditta', header: 'Ditta', size: 2, enableEditing: false, },
  { accessorKey: 'coddip', header: 'Dip', size: 2, enableEditing: false, },
  {
  accessorKey: 'datareg',
  header: 'Data',
  size: 2,
  filterVariant: 'date-range',
  Cell: ({ cell }) => {
    const value = cell.getValue<string>();
    if (!value) return '';

    return new Date(value).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  },
},


  { accessorKey: 'desprog', header: 'Progetto', size: 2, enableEditing: false,Cell: ({ cell }) => {const value = (cell.getValue() ?? '') as string; return (<span title={value}> {value.length > 20 ? `${value.substring(0, 20)}...` : value}</span> ); }, },
  { accessorKey: 'desnodo', header: 'Nodo', size: 2, enableEditing: false,Cell: ({ cell }) => {const value = (cell.getValue() ?? '') as string; return (<span title={value}> {value.length > 10 ? `${value.substring(0, 10)}...` : value}</span> ); }, },
  { accessorKey: 'desatt', header: 'Attivita', size: 2, enableEditing: false,Cell: ({ cell }) => {const value = (cell.getValue() ?? '') as string; return (<span title={value}> {value.length > 10 ? `${value.substring(0, 10)}...` : value}</span> ); }, },
  { accessorKey: 'note', header: 'Note', size: 2, enableEditing: false,  Cell: ({ cell }) => {const value = (cell.getValue() ?? '') as string; return (<span title={value}> {value.length > 80 ? `${value.substring(0, 80)}...` : value}</span> ); }, },
  { accessorKey: 'opzione', header: 'Opzione', size: 2, enableEditing: false,},
  { accessorKey: 'oremin', header: 'Ore', size: 2, enableEditing: false,},
  {
  accessorKey: 'oremineff',
  header: 'OreEff',
  size: 2,
  enableEditing: false,
  Cell: ({ row }) => {

    const oreEff = row.original.oremineff ?? "00:00";

    if (!row.original.coddip || !row.original.datareg) {
      return oreEff;
    }

    const date = new Date(row.original.datareg).toISOString().split('T')[0];
    const key = `${(row.original.coddip ?? '').trim()}_${date}`;

    const totMin = orePerDipGiorno.get(key) ?? 0;

    return `${oreEff} / ${minutesToHHMM(totMin)}`;
  },
},

  { accessorKey: 'luogo', header: 'Luogo', size: 2, enableEditing: false,},
], [validationErrors, orePerDipGiorno]);


const openDeleteConfirmModal = (row: MRT_Row<VersOreDTO2>) => {
  if (window.confirm(`ATTENZIONE !!! CANCELLAZIONE DEL ... "${row.original.id}"`)) {
    deleteVersore2({
      id: row.original.id ?? 0,
      dittaorig: row.original.ditta ?? 0,
      progorig: String(row.original.codprog ?? '').trim(),
      nrrelorig: row.original.id ?? 0,
    });
  }
};


  const openAddModal = () => setIsDialogAddOpen(true);

  const openAddVoiceModal = () => setIsDialogAddVoiceOpen(true);

  const openEditModal = (row: MRT_Row<VersOreDTO2>) => {
    setIsDialogEditOpen(true);
    setCurrentRowId(row.original.id ?? 0);
  };




  const table = useMaterialReactTable({
    columns,
    data: normalizedResults,
    initialState: { density: 'compact'},
    createDisplayMode: 'modal',
    editDisplayMode: 'modal',
    enableEditing: true,
    enableColumnActions: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    getRowId: (row) => row.id?.toString() ?? '',
    muiToolbarAlertBannerProps: isLoadingVersOreDTO2Error
      ? {
          color: 'error',
          children: 'Errore nel caricamento dei dati',
        }
      : undefined,
    muiTableContainerProps: { sx: { minHeight: '500px' } },
    // muiTableBodyCellProps: {  sx: { paddingX: '1px', }, },
    // muiTableHeadCellProps: {  sx: { paddingX: '1px', }, },

    muiSearchTextFieldProps: {
        placeholder: 'NNREL,Ref,Prog,Note',
        sx: { minWidth: "600px", fontSize: '0.75rem', },
        variant: 'outlined',
      },

     // per compattazione tabella padding first above and below second left and right
     muiTableBodyCellProps: {
          sx: {
            padding: '0.5px 4px',
            fontSize: '.9rem',
          },
        },

     muiTableHeadCellProps: {
          sx: {
            padding: '0.5px 4px',
            fontSize: '0.9rem',
          },
        },

// fine compattazione vedi anche row actions
    muiTableBodyRowProps: ({ row }) => ({
        sx: { backgroundColor: row.original.variante ? '#fff3cd' : 'inherit',  },
    }),


    renderTopToolbarCustomActions: () => (
      <>
            <Stack
              direction="row"
              spacing={0}
              sx={{
                width: '60%',
                padding: '4px',
                alignItems: 'center',
              }}
            >
              <Box sx={{ width: '25%', textAlign: 'right' }}>
                 <Button
                    variant="contained"
                    onClick={openAddModal}
                    sx={{ width: 160 }}
                  >
                   Nuovo
                  </Button>

              </Box>
            <Box>
            {coddip === 'MP' && (
                      <Button
                        variant="contained"
                        onClick={openAddVoiceModal}
                        sx={{ width: 160 }}
                      >
                        Nuovo Voice
                      </Button>
                    )}

              </Box>

                <Box sx={{ width: '25%', textAlign: 'right' }}>
  <Button
    variant="contained"
    onClick={() => {
      const newValue = duplicati === 1 ? 0 : 1; // 1 = tutti, 0 = non duplicati
      setDuplicati(newValue);

      setPagination((prev) => ({
        ...prev,
        pageIndex: 0, // 👈 fondamentale
      }));

      setColumnVisibility((prev) => ({
        ...prev,
        variante: newValue === 1,
      }));
    }}
    sx={{ width: 160 }}
  >
    {duplicati === 1 ? 'Nascondi Dup.' : 'Mostra Dup.'}
  </Button>
</Box>



   <Box sx={{ width: '25%', textAlign: 'right' }}>
                <Button
                  variant="contained"
                  onClick={() => setIsDialogExporterOpen(true)}
                  startIcon={<FileDownloadIcon />}
                  sx={{ width: 160 }}
                >
                  EXPORT DATI
                </Button>
                  {isDialogExporterOpen && (
                        <ExpVersOre2
                          pagination={pagination}
                          columnFilters={columnFilters}
                          globalFilter={globalFilter}
                          sorting={sorting}
                          columnVisibility={columnVisibility}
                          columns={columns}
                          onClose={() => setIsDialogExporterOpen(false)}
                        />)}

                </Box>


              <Box sx={{ width: '25%', textAlign: 'right' }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setColumnFilters(draftColumnFilters);
                    saveTabSettings(columnVisibility, draftColumnFilters, sorting, pagination);
                  }}
                  sx={{ width: 160 }}
                >
                  Applica Filtri
                </Button>
              </Box>
            </Stack>

                  </>
                ),


    //
    // renderRowActions: ({ row }) => (
    //   <Box sx={{ display: 'flex', gap: '0.2rem' }}>
    //     <Tooltip title="Modifica">
    //       <IconButton onClick={() => openEditModal(row)}>
    //         <EditIcon />
    //       </IconButton>
    //     </Tooltip>
    //     <Tooltip title="Rimuovi">
    //       <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
    //         <DeleteIcon />
    //       </IconButton>
    //     </Tooltip>
    //   </Box>
    // ),
    //
 renderRowActions: ({ row }) => (
  <Box sx={{ display: 'flex', gap: '2px' }}>
    <Tooltip title="Modifica">
      <IconButton size="small" onClick={() => openEditModal(row)}>
        <EditIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>

    <Tooltip title="Rimuovi">
      <IconButton size="small" color="error" onClick={() => openDeleteConfirmModal(row)}>
        <DeleteIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>
  </Box>
),

    renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
      <>
        <DialogTitle variant="h3">Crea Nuovo</DialogTitle>
        <DialogContent sx={{ width: '1000px' }}>{internalEditComponents}</DialogContent>
        <DialogActions>
          <MRT_EditActionButtons variant="text" table={table} row={row} />
        </DialogActions>
      </>
    ),
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => (
      <>
        <DialogTitle variant="h3">Modifica</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {internalEditComponents}
        </DialogContent>
        <DialogActions>
          <MRT_EditActionButtons variant="text" table={table} row={row} />
        </DialogActions>
      </>
    ),
    state: {
      columnFilters,
      // columnFilters: immediateColumnFilters,  //per gestire il ritardo dell input  MP
      globalFilter,
      pagination,
      sorting,
      columnVisibility: effectiveColumnVisibility,
      isLoading: !isTabSetReady || isLoadingVersOreDTO2,
      // isSaving: isDeletingVersore2,
      showAlertBanner: isLoadingVersOreDTO2Error,
      showProgressBars: isFetchingVersOreDTO2,
    },
    // onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    // onPaginationChange: setPagination,
    // onSortingChange: setSorting,
    onCreatingRowCancel: () => setValidationErrors({}),
    onEditingRowCancel: () => setValidationErrors({}),

    // onColumnFiltersChange: setImmediateColumnFilters,
    onColumnFiltersChange: setDraftColumnFilters,

    // onColumnFiltersChange: (updater) => {
    //   const newFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
    //   setColumnFilters(newFilters);
    //   saveTabSettings(columnVisibility, newFilters, sorting, pagination);
    // },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
      saveTabSettings(columnVisibility, columnFilters, newSorting, pagination);
    },
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
      setPagination(newPagination);
      saveTabSettings(columnVisibility, columnFilters, sorting, newPagination);
    },

    onColumnVisibilityChange: (updater) => {
      const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
      setColumnVisibility(newVisibility);
      saveTabSettings(newVisibility, columnFilters, sorting,pagination);
    },

    rowCount: count ?? 0,
  });

  return (
    <Box sx={{ mt: '60px' }}>
      <MaterialReactTable table={table} />
        {isDialogAddOpen && <AddVersOre2 open onClose={() => setIsDialogAddOpen(false)} />}
        {isDialogAddVoiceOpen && <AddVersOre2Voice open onClose={() => setIsDialogAddVoiceOpen(false)} />}

        {isDialogEditOpen &&  (<EditVersOre2 open id={currentRowId} onClose={() => setIsDialogEditOpen(false)} /> )}

    </Box>
  );
};

const queryClient = new QueryClient();
export default ListaVersOre2;
